// Named webhook (account-scoped) + per-repo binding.
// Source: `docs/idea/integrations.md`, `docs/idea/webhooks.md`.

import { z } from 'zod';
import { vetPublicHttpUrl } from '../value-objects/host-vetting';
import {
  AccountIdSchema,
  RepoIdSchema,
  WebhookBindingIdSchema,
  WebhookIdSchema,
} from '../value-objects/ids';

/** Outbound event types we POST to maintainer-owned URLs. Stable contract. */
export const OutboundEventType = {
  InstallationActivated: 'installation.activated',
  PolicyInvalid: 'policy.invalid',
  IssueReceived: 'issue.received',
  IssueQualified: 'issue.qualified',
  IssueHandoff: 'issue.handoff',
  IssueEscalated: 'issue.escalated',
  IssueClosed: 'issue.closed',
  PrReceived: 'pr.received',
  PrReady: 'pr.ready',
  PrMerged: 'pr.merged',
  ReleaseShipped: 'release.shipped',
  DigestWeekly: 'digest.weekly',
  /** Synthetic dashboard "send test" ping (#807). Never fired organically. */
  TestPing: 'test.ping',
} as const;
export const OutboundEventTypeSchema = z.enum(OutboundEventType);
export type OutboundEventType = z.infer<typeof OutboundEventTypeSchema>;

/**
 * The outbound event types a webhook may SUBSCRIBE to — the ones a producer
 * actually fans out. This is the single seam for that question, and both halves
 * of it are type-locked to this list:
 *
 * | Event | Producer |
 * |---|---|
 * | `issue.escalated` | notify.fanout `escalation` and `decision_needed` (`KIND_TO_OUTBOUND`, `./notification-outbound.ts`) |
 * | `issue.handoff` | `fireWebhook` (the `handoff.fire_webhook` tool) + the forced-handoff default target (`apps/api`) |
 * | `pr.merged` | notify.fanout `pr_merged` |
 *
 * A producer is necessary but NOT sufficient: the subscription only means
 * something if some path reads the webhook's `events`. {@link SUBSCRIPTION_READERS}
 * names that path per event and is exhaustive by type — see it before adding a
 * member here.
 *
 * The other ten members of {@link OutboundEventType} are DECLARED and never
 * emitted: nine (`installation.activated`, `policy.invalid`, `issue.received`,
 * `issue.qualified`, `issue.closed`, `pr.received`, `pr.ready`,
 * `release.shipped`, `digest.weekly`) have no producer at all, and `test.ping`
 * is delivered only by the dashboard's test button, which addresses ONE webhook
 * by id and never reads a subscription — so subscribing to it is a knob nothing
 * consults. Accepting any of them stored a subscription that can never fire,
 * indistinguishable from an endpoint that is simply quiet, which is why
 * {@link CreateWebhookSchema} refuses them at the edge instead.
 *
 * **Adding a producer means adding its event here, in the same change.**
 * `WebhookDispatcher.deliverEvent` and `listWebhooksForEvent` (`@developerz/db`)
 * both take this narrowed type, so fanning out an event absent from this list is
 * a compile error rather than a delivery nobody could have subscribed to — and
 * the entry that fixes it is the same one that opens subscription.
 */
export const SUBSCRIBABLE_OUTBOUND_EVENT_TYPES = [
  OutboundEventType.IssueEscalated,
  OutboundEventType.IssueHandoff,
  OutboundEventType.PrMerged,
] as const;

/**
 * WHO reads a subscription, per subscribable event — the second half of the
 * contract above, kept as DATA rather than prose because prose is what let the
 * first half be misread.
 *
 * `SUBSCRIBABLE_OUTBOUND_EVENT_TYPES` promises a caller "tick this and your
 * endpoint hears about it". The only thing that can keep that promise is a code
 * path which reads the webhook's own `events` array — `listWebhooksForEvent`
 * (`@developerz/db`), directly or through `WebhookDispatcher.deliverEvent`. A
 * producer that resolves ONE named target instead (the `fireWebhook` shape) does
 * not, so its event being "organic" says nothing about whether subscribing to it
 * does anything.
 *
 * `issue.handoff` is exactly that trap and it survives it: `fireWebhook` resolves
 * `webhook_ref` by name, but `POST /v1/issues/:id/handoff` with NO ref picks the
 * account's `issue.handoff` subscriber (`apps/api` `issueActions.forceHandoff` →
 * `listWebhooksForEvent`). The subscription is the DEFAULT-TARGET election, which
 * is a reader — so the event is correctly subscribable, and a reviewer reading
 * only the worker's `NEVER_ORGANIC` note ("delivered by a different dispatcher")
 * would reasonably conclude the opposite. This table is what makes it checkable.
 *
 * `satisfies Record<SubscribableOutboundEventType, …>` is the enforcement:
 * widening the tuple above without naming the path that reads the new
 * subscription is a COMPILE error, not a silent endpoint.
 */
export const SUBSCRIPTION_READERS = {
  [OutboundEventType.IssueEscalated]: {
    via: 'fanout',
    reader:
      'apps/worker notify.fanout — KIND_TO_OUTBOUND.escalation and .decision_needed -> WebhookDispatcher.deliverEvent -> listWebhooksForEvent',
  },
  [OutboundEventType.IssueHandoff]: {
    via: 'direct',
    reader:
      'apps/api issueActions.forceHandoff — listWebhooksForEvent(account, issue.handoff) elects the default target when POST /v1/issues/:id/handoff names no webhook_ref',
  },
  [OutboundEventType.PrMerged]: {
    via: 'fanout',
    reader:
      'apps/worker notify.fanout — KIND_TO_OUTBOUND.pr_merged -> WebhookDispatcher.deliverEvent -> listWebhooksForEvent',
  },
} as const satisfies Record<SubscribableOutboundEventType, SubscriptionReader>;

/** How a subscribable event's `events` array actually gets read. */
export type SubscriptionReader = {
  /**
   * `fanout` — the notification lane's webhook channel (`notify.fanout`).
   * `direct` — a producer outside that lane, so a catalogue check over
   * `KIND_TO_OUTBOUND` can never see it and must not treat its absence as a gap.
   */
  readonly via: 'fanout' | 'direct';
  /** The code path that reads the subscription. Grep-able, never blank. */
  readonly reader: string;
};

/**
 * The subscribable set as a validator. The refusal names the value AND the
 * whole deliverable set — a caller picking from `docs/idea/integrations.md`
 * needs to know which of their picks we refuse and what to pick instead.
 */
export const SubscribableOutboundEventTypeSchema = z.enum(SUBSCRIBABLE_OUTBOUND_EVENT_TYPES, {
  error: (issue) =>
    `developerz.ai does not deliver "${String(issue.input)}" yet — subscribable events: ${SUBSCRIBABLE_OUTBOUND_EVENT_TYPES.join(', ')}`,
});
export type SubscribableOutboundEventType = z.infer<typeof SubscribableOutboundEventTypeSchema>;

export const WebhookSchema = z.object({
  id: WebhookIdSchema,
  accountId: AccountIdSchema,
  /** Unique per account. Referenced from `.maintainer.yml` via `webhook_ref`. */
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, 'lowercase, digits, _ or -'),
  url: z.string().url(),
  /** Envelope-encrypted HMAC signing secret. */
  encryptedSecret: z.instanceof(Uint8Array),
  /**
   * The FULL vocabulary, deliberately wider than {@link CreateWebhookSchema}'s:
   * this parses a STORED row, and a row written before an event's producer was
   * retired (or before the create door narrowed) must still read back rather
   * than fail the whole projection. Narrowing a write is a refusal; narrowing a
   * read is data loss.
   */
  events: z.array(OutboundEventTypeSchema).min(1),
  defaultForRepos: z.array(RepoIdSchema),
  healthy: z.boolean(),
  lastDeliveryAt: z.date().nullable(),
  createdAt: z.date(),
});
export type Webhook = z.infer<typeof WebhookSchema>;

/**
 * The `POST /v1/webhooks` create body (#807). The signing secret is minted +
 * sealed server-side (never client-supplied) and returned once, so it is NOT a
 * field here — only the caller-owned name / url / subscription set.
 *
 * `events` is the SUBSCRIBABLE set, not the full vocabulary: a subscription to
 * an event nothing emits is accepted-then-silent, which reads exactly like a
 * broken endpoint. Refuse at the edge instead, naming what we do deliver.
 */
export const CreateWebhookSchema = z.object({
  name: WebhookSchema.shape.name,
  /**
   * The SSRF gate, at the seam the value enters — the same {@link vetPublicHttpUrl}
   * `S3ConfigSchema.endpoint` runs, because a delivery target is a third URL the
   * platform dials on a customer's behalf and a second SSRF posture is how the
   * blocklist forks.
   *
   * `z.string().url()` alone accepted `javascript:`, `ftp:`, `data:`,
   * `http://169.254.169.254/`, and any in-cluster name. `packages/webhooks`'
   * `deliver.ts` refuses every one of them at dial, so nothing was ever reachable
   * — but the create still answered 201 and handed back the one-time signing
   * secret, then burned all nine attempts over ~44h before flipping the hook
   * unhealthy, leaving the maintainer a health flag instead of the sentence that
   * says what is wrong with their URL.
   *
   * Deliberately NOT on {@link WebhookSchema.url}: that one parses a STORED row,
   * and narrowing a read is data loss — a row captured before this gate has to
   * project. Narrowing the WRITE is the refusal, exactly as `events` does above.
   */
  url: z.string().superRefine((raw, ctx) => {
    const verdict = vetPublicHttpUrl(raw, 'url');
    if (!verdict.ok) ctx.addIssue({ code: 'custom', message: verdict.reason });
  }),
  events: z.array(SubscribableOutboundEventTypeSchema).min(1),
});

/** Repo-scoped binding — narrower event filter than the webhook default. */
export const WebhookBindingSchema = z.object({
  id: WebhookBindingIdSchema,
  repoId: RepoIdSchema,
  webhookId: WebhookIdSchema,
  /** If empty, inherit `webhook.events`. */
  events: z.array(OutboundEventTypeSchema),
  createdAt: z.date(),
});
export type WebhookBinding = z.infer<typeof WebhookBindingSchema>;

/**
 * Invariant: a binding may only narrow, never widen, its parent webhook's
 * event set.
 */
export function bindingNarrowsWebhook(binding: WebhookBinding, webhook: Webhook): boolean {
  if (binding.events.length === 0) return true;
  const parent = new Set(webhook.events);
  return binding.events.every((e) => parent.has(e));
}

// ─── Outbound delivery (HMAC + retries + per-event filter) ───────────────────
// Source: `docs/idea/webhooks.md` § Outbound. The signed wire body, the retry
// backoff, and the subscription filter are pure here; the worker owns the HMAC
// (node:crypto), the HTTP POST, and the `webhook_deliveries` row.

/**
 * The signed POST body — one platform event delivered to a maintainer URL. The
 * `id` prefix is `evt_` per `docs/idea/webhooks.md` (a *receiver*-facing id used
 * for at-least-once dedup on their side); it is a plain string, deliberately NOT
 * the branded `AuditEventId` (a different, internal namespace that shares the
 * prefix). The worker signs the canonicalized form of this object so a retry
 * re-sends byte-identical bytes and recomputes the same signature.
 */
export const OutboundEventEnvelopeSchema = z.object({
  /** Stable event id (`evt_…`) — receiver idempotency key; same across retries. */
  id: z.string().regex(/^evt_[A-Za-z0-9]+$/, 'expected prefix "evt_"'),
  type: OutboundEventTypeSchema,
  /** ISO-8601 instant the event occurred. */
  occurred_at: z.string().datetime(),
  /** Event body. Shape is `type`-specific; receivers narrow on `type`. */
  data: z.record(z.string(), z.unknown()),
});
export type OutboundEventEnvelope = z.infer<typeof OutboundEventEnvelopeSchema>;

/**
 * Retry backoff for a failed delivery, in ms — 1s, 5s, 30s, 5m, 30m, 2h, 12h,
 * 24h (`docs/idea/webhooks.md`). Eight delays → nine total attempts (one initial
 * + eight retries); the ninth failure marks the webhook unhealthy.
 */
export const WEBHOOK_RETRY_SCHEDULE_MS = [
  1_000, // 1s
  5_000, // 5s
  30_000, // 30s
  300_000, // 5m
  1_800_000, // 30m
  7_200_000, // 2h
  43_200_000, // 12h
  86_400_000, // 24h
] as const;

/** Total delivery attempts before a webhook is declared unhealthy (1 + 8 retries). */
export const WEBHOOK_MAX_ATTEMPTS = WEBHOOK_RETRY_SCHEDULE_MS.length + 1;

/**
 * Delay (ms) until the next attempt after `attempt` (1-based) has just failed,
 * or `null` when the schedule is exhausted (→ unhealthy). `attempt` is the count
 * of attempts made so far, so a just-failed first attempt returns 1s and the
 * ninth returns null.
 */
export function webhookRetryDelayMs(attempt: number): number | null {
  if (attempt < 1) return WEBHOOK_RETRY_SCHEDULE_MS[0];
  return WEBHOOK_RETRY_SCHEDULE_MS[attempt - 1] ?? null;
}

/** The next `webhook_deliveries` row state after one attempt resolves. */
export type WebhookDeliveryState = {
  /** Attempts made so far (the one just resolved is included). */
  attempt: number;
  /** Set once a 2xx lands; null while still pending/failed. */
  deliveredAt: Date | null;
  /** When the retry scanner should re-attempt; null when delivered or exhausted. */
  nextRetryAt: Date | null;
  /** True when the retry schedule ran out without a 2xx (→ mark unhealthy). */
  exhausted: boolean;
};

/**
 * Pure transition for one delivery attempt. `attempt` is the just-made attempt
 * number (1-based). A 2xx delivers; a failure either schedules the next attempt
 * (`nextRetryAt = now + delay`) or, when the schedule is exhausted, gives up
 * (`nextRetryAt = null`, `exhausted = true`). Clock is injected so the branch is
 * testable without wall time.
 */
export function nextWebhookDeliveryState(input: {
  attempt: number;
  ok: boolean;
  now: Date;
}): WebhookDeliveryState {
  const { attempt, ok, now } = input;
  if (ok) {
    return { attempt, deliveredAt: now, nextRetryAt: null, exhausted: false };
  }
  const delay = webhookRetryDelayMs(attempt);
  if (delay === null) {
    return { attempt, deliveredAt: null, nextRetryAt: null, exhausted: true };
  }
  return {
    attempt,
    deliveredAt: null,
    nextRetryAt: new Date(now.getTime() + delay),
    exhausted: false,
  };
}

/**
 * Per-event filter: does a webhook subscribed to `events` receive `type`? The
 * DB query pre-filters, so this is the in-process assertion of the same rule for
 * the dispatcher/tests (and documents intent at the seam).
 */
export function webhookSubscribesTo(events: readonly string[], type: OutboundEventType): boolean {
  return events.includes(type);
}
